/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { EntityVersion, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';
import { ContactDeliveryMethod } from '../contacts/model/contact-details-shared.dto';
import { SalesFolderStatus } from './sales-folder.dto';

export const QuotationBase = createEnum('QUOTE_BASE', {
  HouseEstimate: 'HOUSE_ESTIMATE',
  QuickQuote: 'QUICK_QUOTE',
  SalesEstimate: 'SALES_ESTIMATE',
});

export type QuotationBase = EnumSet<typeof QuotationBase>;

export type QuoteSalesTaxRateDto = { salesTaxRateId: ID; ratePct: number };

export type QuotationDto = {
  id: ID;
  code: number;
  name: string;
  date: Date;
  base: QuotationBase;
  priceBookName: string;
  siteName: string;
  price: number;
  version: EntityVersion;
  isApproved: boolean;

  // for complete a sale only
  rfqNumber?: number;
  approvedDate?: Date;
  approvedBy?: string;
  lastUpdated: Date;
  housePlanId: ID;
  housePlanCode: string;
  housePlanName: string;
  isDeleted: boolean;
  inspectionCompleted: boolean;
  rfqPrice1: number;
  rfqPrice2: number;
  memo?: string;
  price1?: number;
};

export type QuotationsGetResponseDto = {
  salesFolderId: ID;
  contactId: ID;
  customerName: string;
  salesFolderName: string;
  quotations: QuotationDto[];
};

export type QuotationApproveDto = {
  approve: boolean;
  version: EntityVersion;
};

export type QuotationVariationDto = {
  quotationItemId: ID;
  itemId: ID;
  itemCode: string;
  isComponent: boolean;
  quantity: number;
  origQuantity: number;
  memo: string;
  description: string;
  costCenterId: ID;
  costCenterCode: string;
  itemPrice: number;
  uomCode: string;
  isInPriceBook: boolean;
  price: number;
  priceWithGst: number;
  isCredit: boolean;
  profit?: number;
  grayOut: boolean;
};

export type QuotationVariationInitResponseDto = QuotationVariationDto;

export type QuotationDetailsBase = WithOptimisticLock<WithVersion<{
  quotationId: ID;
  contactId: ID;
  customerName: string;
  deliveryBy: ContactDeliveryMethod;
  email: string;
  code: string;
  description: string;
  rfqNumber?: number;
  jobNumber?: number;
  priceBookId: ID;
  priceBookName: string;
  siteId: ID;
  siteName: string;
  housePlanId: ID;
  housePlanCode: string;
  housePlanName: string;
  baseHousePlanId: ID;
  baseHousePlanCode: string;
  baseHousePlanName: string;
  memo: string;
  salesFolderId: ID;
  salesFolderName: string;
  salesFolderStatus: SalesFolderStatus;
  created: Date;
  base: QuotationBase;
  isMemoOnTop: boolean;
  approvedByName: string;
  approvedDate?: Date;
  customerSfPlansFolderId: ID;

  pbSalesPrice: number; // PRICE_1
  pbPrice: number; //  PRICE_2

  debit: number; // DEBIT_TOTAL
  credit: number; // CREDIT_TOTAL
  profit: number; // PROFIT_TOTAL

  rfqPrice1: number; // QUOTE_TOTAL_1
  rfqPrice2: number; // QUOTE_TOTAL_2
  pbCost: number;

  origSalesTax: number;
  adjSalesTax: number;
  totalSalesTax: number;

  isSalesEstimateShown: boolean;
  isQuickQuotesAllowed: boolean;
  isGtoTopExist: boolean;
  useSalesEstimate: boolean;
  isCompletionQuote: boolean;
  isRounded: boolean;
  totalPriceWithGst?: number;
  salesTaxRates: QuoteSalesTaxRateDto[];
  // NO PROFIT
}>>;

export type QuotationDetailsInitResponseDto = QuotationDetailsBase &
  { variations: QuotationVariationInitResponseDto[] };

export type QuotationStructureSaveRequestDto = WithDirtyType<{
  quotationItemId?: ID;
  itemId: ID;
  isComponent: boolean;
  description: string;
  costCenterId: ID;
  quantity: number;
  origQuantity: number;
  price: number;
  itemPrice: number;
  isCredit: boolean;
}>;

export type QuotationDetailsSaveRequestDto = WithOptimisticLock<{
  quotationId: ID;
  priceBookId: ID;
  description: string;
  base: QuotationBase;
  siteId: ID;
  baseHousePlanId: ID;
  salesFolderId: ID;
  variations: QuotationStructureSaveRequestDto[];
  salesTaxRates: QuoteSalesTaxRateDto[];
  memo: string;
  isMemoOnTop: boolean;
  isRounded: boolean;
  version: EntityVersion;
  isPersist: boolean;
}>;

export type QuotationVariationsIdsGetResponseDto = {
  itemIds: ID[];
  componentIds: ID[];
};

export type QuotationListItemDto = {
  id: ID;
  code: number;
  date: Date;
  name: string;
  base: QuotationBase;
  priceBookName: string;
  siteName: string;
  price: number;
  rfqPrice1: number;
  version: EntityVersion;
  isApproved: boolean;
};

export type QuotationCloneRequestDto = { skipWithdrawnWarningCheck: boolean };
export type QuotationCloneVariationsDto = { sourceQuoteId: ID; targetQuoteVersion: EntityVersion };

export type QuoteStructureDetailDto = {
  description: string;
  hide: boolean;
};

export type QuoteStructureItemDto = {
  id: ID;
  code: string;
  description: string;
  showQty: boolean;
  showUom: boolean;
  showPrice: boolean;
  hide: boolean;
  details: QuoteStructureDetailDto[];
  isComponent: boolean;
};

export type QuoteStructureHeadingDto = {
  id: ID;
  code: string;
  description: string;
  hide: boolean;
  items: QuoteStructureItemDto[];
  details: QuoteStructureDetailDto[];
};

export type QuoteStructureResponseDto = {
  variations: QuoteStructureItemDto[];
  headings: QuoteStructureHeadingDto[];
  version: EntityVersion;
};

export type QuoteStructureRequestItemDto = {
  id: ID;
  code: string;
  description: string;
  isComponent: boolean;
};

export type QuoteStructureRequestDto = {
  variations: QuoteStructureRequestItemDto[];
  headings: QuoteStructureHeadingDto[];
  version: EntityVersion;
};

export type QuotationRfqNumberDto = {
  quotationId: ID;
  rfqNumber: number;
};

export type QuotationListFilterDto = {
  autocomplete?: boolean;
  searchString?: string;
  showInactive?: boolean;
  salesPersonId?: string;
};
